import { Component, Mixins, Prop, Ref } from 'vue-property-decorator'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import GridHeader from '@/components/GridHeader.vue'
import { initialStatisticAnalysisExternalViewGeneralStatisticsState, StatisticAnalysisExternalViewGeneralStatisticsState } from '@/modules/statistical-analysis-module/components/statistical-analysis-external-views/statistical-analysis-external-views-general-statistics/statistical-analysis-external-views-general-statistics.model'
import { LaboratoryStatisticalAnalysisSidebarDateInput } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-date-and-centers/statistical-analysis-sidebar-date-and-centers.model'
import { StatisticalAnalysisHandlerDatasourceGeneralDossierGeneralList } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-handlers-general-dossier/statistical-analysis-handler-datasource-general-dossier-external-general-list'
import { StatisticalAnalysisGeneralDossierExternalInfo } from '@/modules/statistical-analysis-module/views/general-statistics-dossier/general-statistics-dossier.model'
import { BModal } from 'bootstrap-vue'
import { GetContextMenuItemsParams, GridOptions, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import commonCaseServices from '@/api/resources/common/commonCaseServices'
import { registryCasePrintService } from '@/modules/printModule/services/registry-case-print-service'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { generalStatisticsDossierService } from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.service'
import { ReportGridRequest } from '@/api/services/common.dto'
import BaseCrudInfiniteServerSideComponent from '@/shared/resources/base-crud-infinite-side.component'
import { AgGridCallbackEvent } from '@/shared/models/ag-grid.model'
import { getSelectedRowsIds } from '@/modules/statistical-analysis-module/shared/helpers/helpers'
import { StatisticalAnalysisDatasourceExternalDetail } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-datasource'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisExternalListTypes } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { AxiosResponse } from 'axios'
import { CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { Formatter } from '@/filters/formatter'

@Component({
    components: {
        VtGrid,
        GridDisplayOptions,
        GridHeader
    }
})
export default class StatisticAnalysisExternalViewGeneralStatistics extends Mixins<BaseCrudInfiniteServerSideComponent<StatisticalAnalysisExternalListTypes>>(BaseCrudInfiniteServerSideComponent) {
    @Ref() readonly statisticalAnalysisExternalViewsGeneralStatisticsModal!: BModal
    @Prop() readonly externalInfo!: StatisticalAnalysisGeneralDossierExternalInfo
    stateExternalList!:StatisticAnalysisExternalViewGeneralStatisticsState
    instanceDatasource!: StatisticalAnalysisHandlerDatasourceGeneralDossierGeneralList
    constructor() {
        super()
        this.stateExternalList = initialStatisticAnalysisExternalViewGeneralStatisticsState()
    }

    get gridOptionsTable(): GridOptions {
        return {
            ...this.gridOptions,
            defaultColDef: {
                resizable: true,
                suppressMenu: true
            }
        }
    }

    get laboratoriesSelected(): LaboratoryStatisticalAnalysisSidebarDateInput[] {
        return this.stateExternalList.externalInfo?.laboratories as LaboratoryStatisticalAnalysisSidebarDateInput[]
    }

    get totalElements(): string {
        if (this.state.totalElements) {
            return Formatter.number(this.state.totalElements)
        }
        return '-'
    }

    get gridTitle(): string {
        return `${this.$tc(this.stateExternalList.externalInfo?.trackingGeneralSelected?.label as string)}`
    }

    get typeCase(): string {
        return this.stateExternalList.externalInfo?.typeSearch?.text as string
    }

    get startDate(): Date {
        return this.stateExternalList.externalInfo?.startDate as Date
    }

    get endDate(): Date {
        return this.stateExternalList.externalInfo?.endDate as Date
    }

    get tooltipButton(): string {
        const tooltip = this.$tc('statisticalAnalysis.dossiers.lists.entityList', 1, { entity: this.$tc(this.externalInfo?.typeSearch?.text as string, 2) })
        return Formatter.capitalize(tooltip)
    }

    onHide(): void {
        this.state.totalElements = null
        this.instanceDatasource.setTotalElements(null)
    }

    onChangedMode(mode: any): void {
        this.changedMode(mode)
    }

    onChangedPageSize(size: number): void {
        this.changedPageSize(size)
    }

    onOpenDetailList(): void {
        this.deselectAll()
        this.stateExternalList.externalInfo = this.externalInfo
        this.instanceDatasource = new StatisticalAnalysisHandlerDatasourceGeneralDossierGeneralList(this.stateExternalList.externalInfo)
        this.state.datasource.service = this.instanceDatasource
        this.state.columnsDef = this.instanceDatasource.getGridColumns()
        this.statisticalAnalysisExternalViewsGeneralStatisticsModal.show()
    }

    onEndCallback(_event: AgGridCallbackEvent) {
        this.state.totalElements = this.instanceDatasource.getTotalElements()
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            const selection = this.state.gridApi?.getSelectedRows()!
            const isMultipleSelection = selection?.length > 1
            return [
                {
                    name: this.$tc('global.pathologicalReport'),
                    action: () => this._downloadAPA(params?.node?.data),
                    cssClasses: isMultipleSelection ? ['not-show'] : ['']
                }
            ]
        }
    }

    exportReport(exportType: CommonExportType): Promise<AxiosResponse<Blob>> | null {
        const columns = this.getColumnsToExport()
        if (columns) {
            const entitySelected = this.externalInfo?.typeSearch?.id as StatiscalAnalysisTypeCases
            const entityIdentifiersRequest: EntityIdentifiersRequest = {
                entityIdentifiers: getSelectedRowsIds(entitySelected, this.state.selectedRows)
            }

            const request: ReportGridRequest = {
                sectionLabel: this.gridTitle,
                columns,
                entityIdentifiersRequest
            }
            return generalStatisticsDossierService.generalExportTo(this.externalInfo, request, exportType)
        }
        return null
    }

    private async _downloadAPA(row: StatisticalAnalysisExternalListTypes): Promise<void> {
        const [labId, caseId] = (this.state.datasource.service as StatisticalAnalysisDatasourceExternalDetail<StatisticalAnalysisExternalListTypes>).getLabAndCaseId(row)
        if (caseId) {
            let includeAssociated = false
            if (await this._hasAssociatedCases(labId, caseId)) {
                includeAssociated = await this.$_showConfirmationModal(this.$t('global.modal.case.viewAssociatedCasesConfirmation') as string)
            }
            this.$_updateLoading(true)
            const blob = await this.$_toCallSecure(registryCasePrintService.downloadCaseAPReportPdf(labId, caseId, includeAssociated))
            if (blob) {
                this.$_openFile({ blob })
            }
            this.$_updateLoading(false)
        }
    }

    private async _hasAssociatedCases(labId:number, caseId:number):Promise<boolean> {
        const response = await this.$_toCallSecure(commonCaseServices.getAssociatedCases(labId, caseId))
        return !!response?.length
    }
}
